/* @flow */

import { compose } from 'ramda';
import * as React from 'react';
import { intlShape, injectIntl } from 'react-intl';
import { createUseStyles } from 'react-jss';
import { getUserAvatar } from '@e180/domain/lib/user/util';

import styles from 'src/shared/app/signup/component/profile/base/BaseSignupProfile.style';
import messages from 'src/shared/app/signup/l10n/avatarSignupL10n';
import LazyPictureUploadSection from 'src/shared/app/account/profile/component/upload/LazyPictureUploadSection';
import PictureUploadButton from 'src/shared/app/account/profile/component/upload/PictureUploadButton';
import ZIndexTopBar from 'src/shared/app/base/component/template/component/TopBar';
import TopBar, {
  backButton,
} from 'src/shared/app/nav/component/top-bar/TopBar';
import UserIntro from 'src/shared/components/domain/user/details/UserIntro';
import SecondaryButton from 'src/shared/ui/component/button/SecondaryButton';
import DiscreteLink from 'src/shared/ui/component/link/DiscreteLink';
import InstructionText from 'src/shared/ui/component/text/InstructionText';

import type { User } from '@e180/domain/lib/user/type';

type Props = {
  user: User,
  handleSubmit: () => void,
  handleBack: () => void,
  // Self passed
  // $FlowIssue
  intl: intlShape,
};

const useStyles = createUseStyles(styles);

const SignupAvatarSectionDumb = ({
  user,
  handleBack,
  handleSubmit,
  intl,
}: Props): React.Node => {
  const doesAvatarExist = !!Object.keys(getUserAvatar(user) || {}).length;

  /*
   |----------------------------------------------------------------------------
   | State
   |----------------------------------------------------------------------------
   */

  const [picture, setPicture] = React.useState(null);

  /*
   |----------------------------------------------------------------------------
   | Effects
   |----------------------------------------------------------------------------
   */

  // React.useEffect(() => {
  //   LazyPictureUploadSection.preload();
  // }, []);

  /*
   |----------------------------------------------------------------------------
   | Event Handlers
   |----------------------------------------------------------------------------
   */

  const onInputChange = (file) => setPicture(file);

  const onClose = () => setPicture(null);

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes: Object = useStyles();

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  if (picture) {
    return (
      <LazyPictureUploadSection
        title={intl.formatMessage(messages.addPhoto)}
        picture={picture}
        handleClose={onClose}
      />
    );
  }

  const submitBtn = doesAvatarExist ? (
    <SecondaryButton
      layoutClass={classes.avatarSubmitBtn}
      handleClick={handleSubmit}
    >
      {intl.formatMessage(messages.submit)}
    </SecondaryButton>
  ) : (
    <div className={classes.avatarSubmitBtn}>
      <PictureUploadButton
        layoutClass={classes.pictureBtn}
        label={intl.formatMessage(messages.addPhoto)}
        handleChange={onInputChange}
      />
    </div>
  );

  const skipElt = doesAvatarExist ? null : (
    <div className={classes.helper}>
      <DiscreteLink href="#" handleClick={handleSubmit}>
        {intl.formatMessage(messages.skip)}
      </DiscreteLink>
    </div>
  );

  return (
    <div className={classes.root}>
      <ZIndexTopBar>
        <TopBar
          title={intl.formatMessage(messages.heading)}
          handleCancel={handleBack}
          variant={backButton}
        />
      </ZIndexTopBar>
      <div className={classes.legend}>
        <InstructionText>{intl.formatMessage(messages.helper)}</InstructionText>
      </div>
      <div className={classes.intro}>
        <UserIntro
          user={user}
          hideStats
          hideSocial
          hideConnectedBadge
          useDefaultAvatar
        />
      </div>
      {submitBtn}
      {skipElt}
    </div>
  );
};

export default compose(React.memo, injectIntl)(SignupAvatarSectionDumb);
